/**
 * upgrade-impact.ts — queue #692 §UAT-2026-07-14-R692 §R692-2/§R692-3.
 *
 * 순수 셀렉터 — `module-maturity.json`(commits[].breaking · versionTimeline[]) 과
 * 수기 큐레이션 `module-breaking-changes.json` 을 조합해 "내가 쓰는 버전 이후 업그레이드
 * 영향도"를 판정한다. 신규 데이터 원천/네트워크 호출 0(`recent-changes.ts` 순수-파생
 * 패턴 계보 · `now` 시한폭탄 없음 — 이 셀렉터는 날짜 비교에 wall-clock 을 쓰지 않는다,
 * 전부 커밋/타임라인 자체 `date` 문자열 비교뿐이다).
 *
 * 병합 규칙(§R692-2(c)): 소스①(커밋 `breaking:true`) ∪ 소스③(큐레이션). 동일 `hash` 면
 * 큐레이션이 우선(서술이 풍부) — 큐레이션은 `hash` 없이 `date` 만으로도 유효(backfill).
 */
import type { ModuleMaturity, ModuleBreakingChanges } from '../types/showcase';

/** 업그레이드 영향 항목 1건 — breaking 커밋(소스①) ∪ 큐레이션(소스③) 병합 결과. */
export interface UpgradeBreakingItem {
  /** yyyy-MM-dd */
  date: string;
  hash: string | null;
  /** 큐레이션 summary 우선, 없으면 커밋 subject */
  summary: string;
  affectedApis: string[];
  /** 큐레이션 없으면 null — 렌더가 noDetail 안내로 대체 */
  action: string | null;
  /** 큐레이션 매칭(또는 큐레이션 자체) 여부 — false 면 커밋 마커만으로 판정(영향/조치 미상) */
  hasDetail: boolean;
}

export interface UpgradeImpact {
  /** 선택(또는 기본값=최신) 버전. `versionTimeline` 부재 시 null(폴백 모드). */
  fromVersion: string | null;
  /** `versionTimeline[0].version` — 부재 시 null. */
  latestVersion: string | null;
  /** 필터 기준(anchor) 일자 — null 이면 필터 없음(전체 커밋 기준, 폴백 모드). */
  sinceDate: string | null;
  /** anchor 이후(> sinceDate) 커밋 수(폴백 모드는 전체 커밋 수). */
  totalChanges: number;
  /** 최신순 정렬된 breaking 항목 목록. */
  breakingItems: UpgradeBreakingItem[];
  hasBreaking: boolean;
  /** 마지막(가장 최근) breaking 항목 이후의 변경 건수 — breaking 0건이면 totalChanges 와 동일. */
  changesAfterLastBreaking: number;
  /** `versionTimeline` 이 비어 있어(또는 1개뿐이라 선택 의미가 없어) 전체 커밋 기준으로 폴백했는지. */
  usingFallback: boolean;
}

/**
 * 단일 모듈(artifact) 업그레이드 영향도 판정. 대상 artifact 이력 부재·`gitAvailable:false`·
 * `commits` 0건이면 `null`(호출부가 섹션 미렌더 — §R665/§R666/§R672 0건 숨김 계보 재사용).
 *
 * `fromVersion` — 사용자가 선택한 "내가 쓰는 버전"(select value). `null` 이면 기본값(최신
 * 버전, `versionTimeline[0]`)을 사용한다. `versionTimeline` 이 비어 있으면(또는 1개뿐이면)
 * 버전 선택 자체가 무의미하므로 폴백(전체 커밋 기준 · `usingFallback: true`)한다.
 */
export function selectUpgradeImpact(
  maturity: ModuleMaturity,
  breakingCuration: ModuleBreakingChanges,
  artifact: string,
  fromVersion: string | null
): UpgradeImpact | null {
  const entry = maturity.modules?.[artifact];
  if (!entry || !entry.gitAvailable || !entry.commits?.length) return null;

  const timeline = entry.versionTimeline ?? [];
  const usingFallback = timeline.length <= 1;

  let resolvedFromVersion: string | null = null;
  let latestVersion: string | null = null;
  let sinceDate: string | null = null;

  if (!usingFallback) {
    latestVersion = timeline[0].version;
    const selected = fromVersion != null ? timeline.find((p) => p.version === fromVersion) : undefined;
    const anchor = selected ?? timeline[0];
    resolvedFromVersion = anchor.version;
    sinceDate = anchor.date;
  }

  const candidateCommits = entry.commits.filter((c) => sinceDate == null || c.date > sinceDate);
  const totalChanges = candidateCommits.length;

  const curationEntries = (breakingCuration[artifact] ?? []).filter(
    (c) => sinceDate == null || c.date > sinceDate
  );
  const curationByHash = new Map(
    curationEntries.filter((c): c is typeof c & { hash: string } => !!c.hash).map((c) => [c.hash, c])
  );
  const usedHashes = new Set<string>();

  const items: UpgradeBreakingItem[] = [];

  for (const commit of candidateCommits) {
    if (!commit.breaking) continue;
    const curated = commit.hash ? curationByHash.get(commit.hash) : undefined;
    if (curated?.hash) usedHashes.add(curated.hash);
    items.push({
      date: commit.date,
      hash: commit.hash ?? null,
      summary: curated?.summary ?? commit.subject,
      affectedApis: curated?.affectedApis ?? [],
      action: curated?.action ?? null,
      hasDetail: !!curated,
    });
  }

  for (const curated of curationEntries) {
    if (curated.hash && usedHashes.has(curated.hash)) continue;
    items.push({
      date: curated.date,
      hash: curated.hash ?? null,
      summary: curated.summary,
      affectedApis: curated.affectedApis,
      action: curated.action,
      hasDetail: true,
    });
  }

  items.sort((a, b) => {
    const byDate = b.date.localeCompare(a.date);
    if (byDate !== 0) return byDate;
    return (a.hash ?? '').localeCompare(b.hash ?? '');
  });

  const hasBreaking = items.length > 0;
  const changesAfterLastBreaking = hasBreaking
    ? candidateCommits.filter((c) => c.date > items[0].date).length
    : totalChanges;

  return {
    fromVersion: resolvedFromVersion,
    latestVersion,
    sinceDate,
    totalChanges,
    breakingItems: items,
    hasBreaking,
    changesAfterLastBreaking,
    usingFallback,
  };
}
